import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TokenTypeEnum } from 'authentication/enums/token-type.enum';
import { AuthenticationService } from 'authentication/services/authentication.service';
import { TokenService } from 'authentication/services/token.service';
import { VisualsDashboardService } from 'dashboard/services/visuals-dashboard/visuals-dashboard.service';
import { EMPTY, Observable, Subject, catchError, exhaustMap, switchMap } from 'rxjs';
import { IS_SHIJI_USER_KEY } from 'shared/constants/general-constants';
import { TOKEN_HEADER_KEY } from 'shared/constants/storage-keys';
import { EventBusService, StorageService, ToastService, UserApiService } from 'shared/services';

const DASHBOARD_RETRY_HEADER = 'X-Dashboard-Retry-Attempt';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
    private dashboardTokenRefresh$: Observable<string> | null = null;

    constructor(
        private tokenService: TokenService,
        private authenticationService: AuthenticationService,
        private userApiService: UserApiService,
        private toastService: ToastService,
        private eventBus: EventBusService,
        private storageService: StorageService,
        private router: Router,
        private visualsDashboardService: VisualsDashboardService,
    ) {}

    public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        let bypassResult: { newRequest: HttpRequest<any>; isBypass: boolean };

        bypassResult = this.checkIfBypassInterceptor(request);

        if (bypassResult.isBypass) {
            return next.handle(bypassResult.newRequest);
        }

        bypassResult.newRequest = this.addTokenToRequest(request);

        bypassResult = this.checkIfBypassInterceptor(bypassResult.newRequest, true);

        if (bypassResult.isBypass) {
            return next.handle(bypassResult.newRequest);
        }

        const isDashboardRequest = request.headers.get('X-Token-Type') === TokenTypeEnum.ICE_DASHBOARD;

        if (isDashboardRequest) {
            return this.handleDashboardRequest(bypassResult.newRequest, next);
        }

        return this.checkAndHandleTokenErrors(bypassResult.newRequest, next);
    }

    private handleDashboardRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const retryAttempt = parseInt(request.headers.get(DASHBOARD_RETRY_HEADER) || '0', 10);

        return next.handle(request).pipe(
            catchError((error) => {
                if (retryAttempt >= 1) {
                    this.redirectToDashboardContent();
                    return EMPTY;
                }

                return this.refreshDashboardTokenAndRetry(request, next, false);
            }),
        );
    }

    private getSharedDashboardTokenRefresh(identityTokenAlreadyRefreshed: boolean): Observable<string> {
        if (this.dashboardTokenRefresh$) {
            return this.dashboardTokenRefresh$;
        }

        const refreshSubject = new Subject<string>();
        this.dashboardTokenRefresh$ = refreshSubject.asObservable();

        this.visualsDashboardService
            .obtainDashboardTokenAsObservable()
            .pipe(
                catchError((error) => {
                    if (identityTokenAlreadyRefreshed) {
                        this.dashboardTokenRefresh$ = null;
                        refreshSubject.error(error);
                        return EMPTY;
                    }

                    return this.authenticationService.renewIdentityTokenAsObservable().pipe(
                        switchMap(() => this.visualsDashboardService.obtainDashboardTokenAsObservable()),
                        catchError((identityError) => {
                            this.dashboardTokenRefresh$ = null;
                            refreshSubject.error(identityError);
                            return EMPTY;
                        }),
                    );
                }),
            )
            .subscribe({
                next: (token) => {
                    this.dashboardTokenRefresh$ = null;
                    refreshSubject.next(token);
                    refreshSubject.complete();
                },
                error: (error) => {
                    this.dashboardTokenRefresh$ = null;
                    refreshSubject.error(error);
                },
            });

        return refreshSubject.asObservable();
    }

    private refreshDashboardTokenAndRetry(
        originalRequest: HttpRequest<unknown>,
        next: HttpHandler,
        identityTokenAlreadyRefreshed: boolean,
    ): Observable<HttpEvent<unknown>> {
        return this.getSharedDashboardTokenRefresh(identityTokenAlreadyRefreshed).pipe(
            switchMap((dashboardToken) => {
                const retryRequest = this.buildDashboardRetryRequest(originalRequest, dashboardToken);
                return next.handle(retryRequest);
            }),
            catchError(() => {
                this.redirectToDashboardContent();
                return EMPTY;
            }),
        );
    }

    private buildDashboardRetryRequest(originalRequest: HttpRequest<unknown>, dashboardToken: string): HttpRequest<unknown> {
        const currentRetry = parseInt(originalRequest.headers.get(DASHBOARD_RETRY_HEADER) || '0', 10);

        return originalRequest.clone({
            headers: originalRequest.headers
                .set(TOKEN_HEADER_KEY, 'Bearer ' + dashboardToken)
                .set(DASHBOARD_RETRY_HEADER, (currentRetry + 1).toString()),
        });
    }

    private redirectToDashboardContent(): void {
        // TODO: Translate
        this.showWarning('generalKeywords.noVisualsDashboardAccess');
        this.router.navigate(['/dashboard/content']);
    }

    public addTokenToRequest(request: HttpRequest<unknown>): HttpRequest<unknown> {
        const tokenType = request.headers.get('X-Token-Type');
        let token: string | null = null;

        switch (tokenType) {
            case TokenTypeEnum.ICE_DASHBOARD:
                token = this.tokenService.iceDashboardToken() || null;
                request = request.clone({
                    headers: request.headers.delete('X-Token-Type'),
                });
                break;
            case TokenTypeEnum.IDENTITY_TOKEN:
                token = this.tokenService.identityToken() || null;
                request = request.clone({
                    headers: request.headers.delete('X-Token-Type'),
                });
                break;
            default:
                token = this.tokenService.token() || null;
                break;
        }

        if (!token) {
            return request;
        }

        return request.clone({
            headers: request.headers.set(TOKEN_HEADER_KEY, 'Bearer ' + token),
        });
    }

    private checkAndHandleTokenErrors(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(request).pipe(
            catchError((error) => {
                // If any request failed, and reached this line, it means the error interceptor skipped it because we need to handle it here
                // If the error status code is contained in the 'redirecttologinpageforcodes' header,
                // then the refresh token request failed, since we manually added that header, and we redirect to the login page
                if (
                    request.headers.getAll('redirecttologinpageforcodes')?.length &&
                    request.headers.getAll('redirecttologinpageforcodes')?.includes(error.status.toString())
                ) {
                    this.showWarningAndRedirectToLogin('generalKeywords.invalidToken');
                    throw error;
                }

                if ([401].includes(error.status)) {
                    this.eventBus.dispatch('tokenChange');
                    return this.handleAuthenticationError(next, request);
                }

                if ([403, 456].includes(error.status)) {
                    this.eventBus.dispatch('tokenChange');
                    return this.handlePermissionError(next, request);
                }

                // It's not an error we want to treat in this interceptor so pass it along
                throw error;
            }),
        ) as Observable<any>;
    }

    public getNewToken() {
        const userType = this.storageService.getFromLocal(IS_SHIJI_USER_KEY) ? 'shiji' : 'supplier';

        if (userType) {
            return this.userApiService.getUserToken(this.storageService.getFromLocal(IS_SHIJI_USER_KEY) as boolean, true).pipe(
                catchError(() => {
                    this.showWarningAndRedirectToLogin('generalKeywords.invalidToken');
                    return EMPTY;
                }),
            );
        } else {
            this.showWarningAndRedirectToLogin('generalKeywords.invalidToken');
            throw '';
        }
    }

    private handleAuthenticationError(next: HttpHandler, request: HttpRequest<unknown>): Observable<any> {
        return this.getNewToken().pipe(
            exhaustMap((token) => {
                this.tokenService.setToken(token);
                const newRequest = this.addTokenToRequest(request);

                return next.handle(newRequest).pipe(
                    catchError((error) => {
                        if ([401].includes(error.status)) {
                            this.showWarning('generalKeywords.unauthorized');
                            return EMPTY;
                        }

                        throw error;
                    }),
                );
            }),
        );
    }

    private handlePermissionError(next: HttpHandler, request: HttpRequest<unknown>): Observable<any> {
        return this.getNewToken().pipe(
            exhaustMap((token) => {
                this.tokenService.setToken(token);
                this.userApiService.getCurrentUser();
                const newRequest = this.addTokenToRequest(request);
                return next.handle(newRequest).pipe(
                    catchError((error) => {
                        if ([401, 403, 456].includes(error.status)) {
                            this.handlePermissionRetryFailure();
                            return EMPTY;
                        }

                        throw error;
                    }),
                );
            }),
        );
    }

    private handlePermissionRetryFailure(): void {
        const currentRoute = this.router.url;
        const isDashboardRoute = this.isDashboard(currentRoute);

        if (isDashboardRoute) {
            this.showWarningAndRedirectToLogin('generalKeywords.invalidPermission');
        } else {
            this.showWarningAndRedirectToDashboard('generalKeywords.invalidPermissionRedirectingToDashboard');
        }
    }

    private isDashboard(route: string): boolean {
        return route === '/dashboard' || route.startsWith('/dashboard/') || route.startsWith('/dashboard?');
    }

    private checkIfBypassInterceptor(
        request: HttpRequest<any>,
        checkIfBypassRetryRequest: boolean = false,
    ): {
        newRequest: HttpRequest<any>;
        isBypass: boolean;
    } {
        if (checkIfBypassRetryRequest) {
            if (
                request.headers.get('isBypassTokenInterceptorRetryRequest') ||
                request.headers.getAll('isBypassTokenInterceptorRetryRequest')
            ) {
                return {
                    newRequest: request.clone({ headers: request.headers.delete('isBypassTokenInterceptorRetryRequest') }),
                    isBypass: true,
                };
            }

            return { newRequest: request, isBypass: false };
        }

        if (request.headers.get('bypassTokenRefreshInterceptor') || request.headers.getAll('bypassTokenRefreshInterceptor')) {
            return {
                newRequest: request.clone({ headers: request.headers.delete('bypassTokenRefreshInterceptor') }),
                isBypass: true,
            };
        }

        return { newRequest: request, isBypass: false };
    }

    private showWarningAndRedirectToLogin(errorMessage: string): void {
        this.showWarning(errorMessage);

        // Timeout OK
        setTimeout(() => {
            this.authenticationService.logout();
        }, 3000);
    }

    private showWarningAndRedirectToDashboard(errorMessage: string): void {
        this.showWarning(errorMessage);
        // Timeout OK
        setTimeout(() => {
            this.router.navigate(['/dashboard']);
        }, 3000);
    }

    private showWarning(errorMessage: string): void {
        this.toastService.showToast({
            body: errorMessage,
            severity: 'warning',
            duration: 5000,
            ignoreRemoval: true,
        });
    }
}
